/**
 * AST 기반 자동 생성된 검증 함수
 * 이 파일은 scripts/generate-validators.ts에 의해 자동 생성됩니다.
 * 수동으로 수정하지 마세요.
 * 
 * 생성 시간: 2026-09-18T08:13:01.399Z
 * 소스 파일: src/data/dto/patient-reponse-dto
 */

import type { PatientResponseDto } from "@/data/dto/patient-reponse-dto";

/**
 * PatientResponseDto 타입 가드 함수
 * AST 분석을 통해 자동 생성된 최적화된 검증 함수
 */
export function isPatientResponseDto(input: unknown): input is PatientResponseDto {
  if (typeof input !== "object" || input === null || Array.isArray(input)) {
    return false;
  }
  
  const obj = input as Record<string, unknown>;
  
  return (
    typeof obj.patientUuid === "string" &&
    typeof obj.patientScreeningId === "string" &&
    typeof obj.patientName === "string" && obj.patientName.trim() !== "" &&
    (obj.patientBirthDate == null || typeof obj.patientBirthDate === "string") &&
    (obj.patientGender == null || typeof obj.patientGender === "string") &&
    typeof obj.isDeactivated === "boolean" &&
    (obj.step === undefined || (obj.step == null || (typeof obj.step === "number" && !isNaN(obj.step)))) &&
    (obj.state === undefined || (obj.state == null || typeof obj.state === "string")) &&
    (obj.memo == null || typeof obj.memo === "string") &&
    typeof obj.canAddTreatment === "boolean" &&
    (obj.adherence === undefined || (obj.adherence == null || (typeof obj.adherence === "number" && !isNaN(obj.adherence))))
  );
}

/**
 * PatientResponseDto 상세 검증 결과 반환 함수
 * 검증 실패 시 상세한 에러 정보를 반환합니다.
 */
export interface IValidation<T> {
  success: boolean;
  data?: T;
  errors?: Array<{ path: string; expected: string; value: unknown }>;
}

export function validatePatientResponseDto(input: unknown): IValidation<PatientResponseDto> {
  const errors: Array<{ path: string; expected: string; value: unknown }> = [];
  
  if (typeof input !== "object" || input === null || Array.isArray(input)) {
    errors.push({
      path: "",
      expected: "object",
      value: input,
    });
    return {
      success: false,
      errors,
    };
  }
  
  const obj = input as Record<string, unknown>;
  
    if (typeof obj !== "object" || obj === null || Array.isArray(obj)) {
      errors.push({ path: "", expected: "object", value: obj });
    } else {
      const objObj = obj as Record<string, unknown>;
      // patientUuid (required)
      if (objObj.patientUuid === undefined) {
        errors.push({ path: "patientUuid", expected: "string", value: undefined });
      } else {
        if (typeof objObj.patientUuid !== "string") {
          errors.push({ path: "patientUuid", expected: "string", value: objObj.patientUuid });
        }
      }
      // patientScreeningId (required)
      if (objObj.patientScreeningId === undefined) {
        errors.push({ path: "patientScreeningId", expected: "string", value: undefined });
      } else {
        if (typeof objObj.patientScreeningId !== "string") {
          errors.push({ path: "patientScreeningId", expected: "string", value: objObj.patientScreeningId });
        }
      }
      // patientName (required)
      if (objObj.patientName === undefined) {
        errors.push({ path: "patientName", expected: "string", value: undefined });
      } else {
        if (!(typeof objObj.patientName === "string" && objObj.patientName.trim() !== "")) {
          errors.push({ path: "patientName", expected: "string (non-empty)", value: objObj.patientName });
        }
      }
      // patientBirthDate (required)
      if (objObj.patientBirthDate === undefined) {
        errors.push({ path: "patientBirthDate", expected: "string", value: undefined });
      } else {
        if (!(objObj.patientBirthDate == null || typeof objObj.patientBirthDate === "string")) {
          errors.push({ path: "patientBirthDate", expected: "string | null", value: objObj.patientBirthDate });
        }
      }
      // patientGender (required)
      if (objObj.patientGender === undefined) {
        errors.push({ path: "patientGender", expected: "string", value: undefined });
      } else {
        if (!(objObj.patientGender == null || typeof objObj.patientGender === "string")) {
          errors.push({ path: "patientGender", expected: "string | null", value: objObj.patientGender });
        }
      }
      // isDeactivated (required)
      if (objObj.isDeactivated === undefined) {
        errors.push({ path: "isDeactivated", expected: "boolean", value: undefined });
      } else {
        if (typeof objObj.isDeactivated !== "boolean") {
          errors.push({ path: "isDeactivated", expected: "boolean", value: objObj.isDeactivated });
        }
      }
      // step (optional)
      if (objObj.step !== undefined) {
        if (!(objObj.step == null || (typeof objObj.step === "number" && !isNaN(objObj.step)))) {
          errors.push({ path: "step", expected: "number | null", value: objObj.step });
        }
      }
      // state (optional)
      if (objObj.state !== undefined) {
        if (!(objObj.state == null || typeof objObj.state === "string")) {
          errors.push({ path: "state", expected: "string | null", value: objObj.state });
        }
      }
      // memo (required)
      if (objObj.memo === undefined) {
        errors.push({ path: "memo", expected: "string", value: undefined });
      } else {
        if (!(objObj.memo == null || typeof objObj.memo === "string")) {
          errors.push({ path: "memo", expected: "string | null", value: objObj.memo });
        }
      }
      // canAddTreatment (required)
      if (objObj.canAddTreatment === undefined) {
        errors.push({ path: "canAddTreatment", expected: "boolean", value: undefined });
      } else {
        if (typeof objObj.canAddTreatment !== "boolean") {
          errors.push({ path: "canAddTreatment", expected: "boolean", value: objObj.canAddTreatment });
        }
      }
      // adherence (optional)
      if (objObj.adherence !== undefined) {
        if (!(objObj.adherence == null || (typeof objObj.adherence === "number" && !isNaN(objObj.adherence)))) {
          errors.push({ path: "adherence", expected: "number | null", value: objObj.adherence });
        }
      }
    }
  
  if (errors.length === 0 && isPatientResponseDto(input)) {
    return {
      success: true,
      data: input,
    };
  }
  
  return {
    success: false,
    errors: errors.length > 0 ? errors : [
      {
        path: "",
        expected: "PatientResponseDto",
        value: input,
      },
    ],
  };
}

/**
 * PatientResponseDto 검증 후 에러 throw 함수
 * 검증 실패 시 상세한 에러 정보를 포함하여 에러를 throw합니다.
 */
export function assertPatientResponseDto(input: unknown): PatientResponseDto {
  const validation = validatePatientResponseDto(input);
  if (!validation.success) {
    const errorDetails = validation.errors?.map(err => 
      `  - ${err.path}: 예상 타입 ${err.expected}, 실제 값: ${JSON.stringify(err.value)}`
    ).join("\n") || "알 수 없는 오류";
    throw new Error(`[assertPatientResponseDto] :: 검증 실패: PatientResponseDto 타입과 일치하지 않습니다\n\n오류 상세:\n${errorDetails}`);
  }
  return validation.data!;
}
